import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@skygroup/shared/API/apiClient';
import endpoints from '../../utils/endpoints';
import { useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  DynamicPackageFlightViewResponse,
  FlightType,
  FlightViewResponse,
} from '../../interfaces/flightInterface';
export interface ApiError {
  status: number;
  message: string;
  code: number;
}

const calculateTotalPassengersForMultiRooms = (
  guests: any
): { adult: number; child: number; infant: number } => {
  if (!Array.isArray(guests) || guests.length === 0) {
    return { adult: 0, child: 0, infant: 0 };
  }

  return guests.reduce(
    (acc, curr) => {
      acc.adult += +curr.adult || 0;
      acc.child += +curr.child || 0;
      acc.infant += +curr.infant || 0;
      return acc;
    },
    { adult: 0, child: 0, infant: 0 }
  );
};

const fetchFlightView = async (
  identifier: string,
  flightType: FlightType,
  adult: number | string,
  child: number | string,
  infant: number | string
) => {
  const body = {
    flightType,
    f1: identifier,
    adult: Number(adult),
    child: Number(child),
    infant: Number(infant),
  };

  try {
    const response = await apiClient.post(
      `${endpoints.FLIGHTS.VIEW_FLIGHT_ALP}`,
      body
    );
    const payload = response.data;

    if (payload?.status === 'error') {
      throw new Error(payload?.data?.message || 'Unknown error');
    }

    if (flightType === 'one_way') {
      if (payload === '') {
        throw new Error('error');
      }

      if (typeof payload === 'string') {
        throw new Error(payload);
      }

      if (payload.flightsInfo) {
        return payload;
      }
      return {
        flightsInfo: {
          flight: payload?.flight,
          flightFrom: payload?.flight,
          ...payload,
        },
        pax: payload.pax,
      };
    }

    if (typeof payload === 'string') {
      throw new Error(payload);
    }
    return payload;
  } catch (error) {
    if (error instanceof Error && error.message !== 'error') {
      throw error;
    }
    throw new Error('error');
  }
};

export const useViewDynamicPackageFlightApiMs = ({
  urlParams,
  is_dp,
}: {
  urlParams: string;
  is_dp?: 0 | 1;
}) => {
  const flightType = FlightType.BundleFlight;
  // let { multi_room_guests, f } = Object.fromEntries(
  //   new URLSearchParams(urlParams)
  // );

  let { adult, child, infant, multi_room_guests, f } = useSelector(
    (data: RootState) => data.search.searchParams
  );

  const urlF = urlParams
    ? new URLSearchParams(urlParams).get('f')
    : null;
  if (!f && urlF) {
    f = urlF;
  }

  const flightViews = useQuery<DynamicPackageFlightViewResponse, ApiError>(
    ['flight-view-ms-for-dynamic-packages', flightType, is_dp, f, multi_room_guests],
    async () => {
      try {
        const totalPax =
          calculateTotalPassengersForMultiRooms(multi_room_guests);
        const res = await apiClient.post(
          `${endpoints.FLIGHTS.VIEW_FLIGHT_ALP}`,
          {
            flightType,
            f1: f,
            adult: Number(totalPax.adult),
            child: Number(totalPax.child),
            infant: Number(totalPax.infant),
            is_dp,
          }
        );

        if (res?.data?.status === "error" || res.status >= 400 || typeof res?.data === 'string') {
          throw new Error(res?.data?.data?.message || res?.data || 'Unknown error');
        }

        return res?.data;
      } catch (error) {
        throw new Error(JSON.stringify(error));
      }
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: false,
      keepPreviousData: true,
    }
  );

  return flightViews;
};

export const useViewFlightApiMs = ({
  predefinedId,
  predefinedType,
  setIsLoadingFlight,
}: {
  predefinedId?: string | null;
  predefinedType?: FlightType;
  setIsLoadingFlight?: (loading: boolean) => void;
}) => {
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);
  let flightType = pathSegments[2] as FlightType;
  let identifiers: any[] = []; // Split identifiers into an array

  if (predefinedId && predefinedType) {
    identifiers.push(predefinedId);
    flightType = predefinedType;
  } else if (pathSegments[3]) {
    identifiers = pathSegments[3].split(',');
  }

  let { adult, child, infant, multi_room_guests } = useSelector(
    (data: RootState) => data.search.searchParams
  );

  if (predefinedId && predefinedType === FlightType.BundleFlight) {
    const totalPax =
      Array.isArray(multi_room_guests) &&
      multi_room_guests?.length > 0 &&
      multi_room_guests.reduce(
        (acc, curr) => {
          acc.adult += +curr.adult;
          acc.child += +curr.child;
          acc.infant += +curr.infant;
          return acc;
        },
        {
          adult: 0,
          child: 0,
          infant: 0,
        }
      );

    adult = totalPax.adult;
    child = totalPax.child;
    infant = totalPax.infant;
  }

  const flightViews = useQuery<FlightViewResponse[], ApiError>(
    ['flight-view-ms', identifiers, adult, child, flightType, infant],
    async () => {
      setIsLoadingFlight && setIsLoadingFlight(true);
      const allResponsePromises = identifiers.map((identifier) =>
        fetchFlightView(identifier, flightType, adult, child, infant)
      );
      const results = await Promise.all(allResponsePromises);
      setIsLoadingFlight && setIsLoadingFlight(false);

      const errorResult = results.find(
        (result: any) => result?.status === 'error'
      );
      if (errorResult) {
        throw new Error(errorResult?.data?.message || 'Unknown error');
      }

      return results;
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: false,
      keepPreviousData: true,
    }
  );

  return flightViews;
};

const useGetFlightAdditionalFares = (
  tsBookingDataFromProp?: any,
  identifierFromProp?: string
) => {
  const { data: selectedFlight }: any = useViewFlightApiMs({});
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const flightType = pathSegments[2] as FlightType;
  const identifiers = pathSegments[3] ? pathSegments[3].split(',') : [];

  const flightIdentifier = identifierFromProp ?? identifiers[0];

  const matchedFlight = !selectedFlight ? false :
      (selectedFlight?.length > 1 ?
      selectedFlight?.find((flight: any) => flight?.flightsInfo?.identifier === flightIdentifier):
      selectedFlight[0]);

  const ts_booking_data =
    tsBookingDataFromProp ?? matchedFlight?.flightsInfo?.ts_booking_data;
  const isEnabled = !!(ts_booking_data && flightIdentifier);

  const flightViews = useQuery<any>(
    ['additional-fares', flightType, flightIdentifier, ts_booking_data],
    async () => {
      const results = await apiClient.post(
        `${endpoints.FLIGHTS.VIEW_ADDITIONAL_FARES}`,
        {
          flightType: flightType,
          identifier: flightIdentifier,
          ts_booking_data: ts_booking_data,
        }
      );
      if (results.data?.status == 'error') {
        console.log(results.data);
        return [];
      } else {
        return Object.values(results.data?.data);
      }
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: isEnabled,
      keepPreviousData: true,
      staleTime: Infinity,
    }
  );

  return flightViews;
};
export default useGetFlightAdditionalFares;
